<template>
    <div class="ckp-cart">
        <div v-if="showAlertPrepaid" class="ckp-alert is-danger">Sélectionnez le prépaiement à appliquer.</div>
        <div v-if="isClotured" class="ckp-alert is-danger">Cette vente est clôturée.</div>
        <div v-if="releasePrepaidMessage" class="ckp-alert" :class="releasePrepaidMessage.type === 'success' ? 'is-success' : 'is-danger'">
            {{ releasePrepaidMessage.text }}
        </div>

        <!-- Panier -->
        <section class="ckp-block">
            <h3 class="ckp-title">Panier</h3>
            <div v-if="Object.keys(c_cartItems).length === 0" class="ckp-muted">Aucun article dans le panier.</div>
            <div v-for="item in c_cartItems" :key="item.id" class="ckp-line" :class="{ 'is-removed': item.pendingRemoval }">
                <div class="ckp-line-main">
                    <strong>{{ item.qty }} × {{ item.name }}</strong>
                    <small>
                        <i v-if="item.bookingId" class="bi bi-lock" title="Lié à une réservation"></i>
                        <span v-if="item.pendingRemoval" class="ckp-text-danger">Suppression en attente · </span>
                        <span v-else-if="item.pendingAddition" class="ckp-text-success">Ajout en attente · </span>
                        <span v-if="item.voucherCode">Bon {{ item.voucherCode }} · </span>
                        <span v-if="item.options.serie">Abonnement · </span>
                        <span v-if="item.options.treatment">Cure · </span>
                        <button type="button" class="ckp-link" @click="openStaffModal(item.id, item.advised.id, item.seller.id)">
                            {{ item.seller && item.seller.first_name ? 'Praticien·ne ' + item.seller.first_name : 'Sans praticien assigné' }}
                        </button>
                    </small>
                    <div v-if="item.code" class="ckp-codes">
                        <span
                            v-for="(val, index) of item.code"
                            :key="index"
                            class="ckp-code"
                            :class="{ 'is-off': !val.enable }"
                            role="button"
                            @click="() => { val.enable = !val.enable }"
                            :set="(noComplete = c_promoCodeNoCompleteUsed && c_promoCodeNoCompleteUsed.find(x => x.codeId == val.id && item.rowId == x.itemKey))"
                        >
                            <i class="bi bi-ticket-perforated"></i>
                            <template v-if="noComplete">{{ val.title }} · {{ noComplete.used.toFixed(2) }} / {{ noComplete.amount.toFixed(2) }} €</template>
                            <template v-else>{{ val.title }} · {{ val.amount }}</template>
                        </span>
                    </div>
                </div>
                <div class="ckp-line-price">
                    <s v-if="item._oldPrice && item._oldPrice != item.price">{{ euro(item._oldPrice) }}</s>
                    <span :class="{ 'ckp-text-danger': item.pendingRemoval, 'ckp-text-success': item.pendingAddition }">{{ euro(item.price) }}</span>
                </div>
            </div>
        </section>

        <!-- Totaux -->
        <section class="ckp-block">
            <div class="ckp-row"><span>Montant du panier</span><span>{{ euro(effectiveTotal) }}</span></div>
            <div v-if="parseFloat(voucherAlignmentDiscount) > 0" class="ckp-row"><span>Dont remise liée au bon cadeau</span><span>−{{ euro(voucherAlignmentDiscount) }}</span></div>
            <div v-if="pendingRemovalTotal > 0" class="ckp-row ckp-text-danger"><span>Suppressions en attente</span><span>−{{ euro(pendingRemovalTotal) }}</span></div>
            <div v-if="pendingAdditionTotal > 0" class="ckp-row ckp-text-success"><span>Ajouts en attente</span><span>+{{ euro(pendingAdditionTotal) }}</span></div>
            <template v-for="(val, index) of c_payments">
                <div :key="index" v-if="val && parseFloat(val.value)" class="ckp-row"><span>{{ val.name }}</span><span>{{ euro(val.value) }}</span></div>
            </template>
            <div v-if="fidelityHistoryAmount > 0" class="ckp-row"><span>Points de fidélité utilisés</span><span>{{ euro(fidelityHistoryAmount) }}</span></div>
            <div class="ckp-row">
                <span>{{ nonFidelityInvoices.length ? 'Acompte reçu' : 'Déjà encaissé' }}</span>
                <span>{{ euro(nonFidelityInvoices.reduce((prev, current) => prev + paymentAmountAppliedToOrder(current), 0)) }}</span>
            </div>
            <template v-if="reservedPrepaidReservations.length">
                <div class="ckp-row ckp-text-warning"><span>Prépayé en attente</span><span>{{ euro(prepaidReservationsTotal) }}</span></div>
                <div v-for="reservation in reservedPrepaidReservations" :key="reservation.id" class="ckp-row ckp-row-sub">
                    <span>{{ reservation.label }}<template v-if="reservation.voucher_code"> · {{ reservation.voucher_code }}</template></span>
                    <span>
                        {{ euro(prepaidReservationAmount(reservation)) }}
                        <button type="button" class="ckp-icon" title="Libérer ce prépayé" aria-label="Libérer ce prépayé" @click="openReleasePrepaidModal(reservation)"><i class="bi bi-x-lg"></i></button>
                    </span>
                </div>
            </template>
            <div v-if="usedFidelityPoints" class="ckp-row"><span>Remise fidélité ({{ usedFidelityPoints }} pts)</span><span>−{{ euro(usedFidelityPoints / c_establishment.points_for_one_euro) }}</span></div>
            <div class="ckp-row is-total" :class="effectiveRemainPrice < 0 ? 'ckp-text-warning' : ''">
                <span>{{ effectiveRemainPrice < 0 ? 'Trop perçu' : 'Reste dû' }}</span>
                <span>{{ euro(Math.abs(effectiveRemainPrice)) }}</span>
            </div>
            <div v-if="effectiveRemainPrice == 0 && Object.keys(c_cartItems).length" class="ckp-state is-success"><i></i> Panier soldé, prêt à clôturer.</div>
            <div v-if="effectiveRemainPrice < 0" class="ckp-state is-warning"><i></i> Un avoir sera créé pour le trop-perçu.</div>
            <div v-if="accompte != 0" class="ckp-row-group">
                <div v-if="totalAccompte != 0" class="ckp-row"><span>Acompte à payer</span><span>{{ euro(totalAccompte) }}</span></div>
                <div v-if="rest != 0" class="ckp-row"><span>Déjà payé</span><span>{{ euro(rest) }}</span></div>
                <div v-if="rest != 0" class="ckp-row"><span>Reste à payer</span><span>{{ euro(totalWRest) }}</span></div>
            </div>
        </section>

        <input v-for="(val, index) of c_voucherUsed" type="hidden" name="voucher-used[]" :value="stringify(val)" :key="index" />
        <input v-for="(val, index) of prepaidTreatment" type="hidden" name="prepaid-treatment[]" :key="'t' + index" :value="stringify(val)" />
        <input v-for="(val, index) of prepaidSerie" type="hidden" name="prepaid-serie[]" :key="'s' + index" :value="stringify(val)" />

        <!-- Points de fidélité -->
        <section v-if="c_customer && c_customer.customer && c_customer.customer.loyalty_points !== undefined" class="ckp-block">
            <div class="ckp-section-head">
                <h3 class="ckp-title">Points de fidélité</h3>
                <span class="ckp-hint">{{ (c_customer.customer.loyalty_points - (usedFidelityPoints || 0)).toLocaleString('fr-FR') }} disponibles</span>
            </div>
            <div v-if="effectiveRemainPrice <= 0 && !usedFidelityPoints" class="ckp-muted">Rien à régler, les points restent au client.</div>
            <div v-else class="ckp-points">
                <input
                    type="number"
                    class="ckp-amount"
                    name="fidelity_points"
                    id="fidelity_points"
                    min="0"
                    :max="`${maxFidelityPoint}`"
                    step="1"
                    @input="val => { if (parseInt(usedFidelityPoints) > maxFidelityPoint) usedFidelityPoints = `${maxFidelityPoint}` }"
                    v-model="usedFidelityPoints"
                    aria-label="Points à utiliser"
                />
                <span class="ckp-points-value">pts = {{ euro((usedFidelityPoints || 0) / c_establishment.points_for_one_euro) }}</span>
                <button type="button" class="ck-button ck-button-small" @click="useMaxFidelityPoints">Utiliser</button>
            </div>
            <input v-if="effectiveRemainPrice <= 0 && !usedFidelityPoints" type="hidden" name="fidelity_points" :value="usedFidelityPoints" />
        </section>

        <!-- Options et encaissement -->
        <section class="ckp-block">
            <input type="hidden" name="product_deposit_mode" :value="productDepositMode ? 1 : 0" />
            <label class="ckp-check" for="product-deposit-mode">
                <input id="product-deposit-mode" type="checkbox" v-model="productDepositMode" :disabled="productDepositModeLocked" aria-describedby="product-deposit-help" />
                Commande (pros / associations)
            </label>
            <p id="product-deposit-help" class="ckp-note">Les paiements génèrent des factures d’acompte sur la commande ouverte. La facture de clôture est créée à la clôture.</p>
            <p v-if="productDepositModeLocked" class="ckp-note">Ce mode et le destinataire sont verrouillés après le premier acompte.</p>
            <div v-if="sendInvoice || productDepositMode" class="ckp-options">
                <label class="ckp-label" for="customer_society_id">Société ou association sur la facture</label>
                <input type="search" class="ckp-select" placeholder="Rechercher une société ou association" v-model="customer_society_search" />
                <select required class="ckp-select" name="customer_society_id" id="customer_society_id" :disabled="productDepositMode && productDepositModeLocked" v-model="customer_society_id" @change="applySelectedSociety">
                    <option :value="null">Choisir une société ou association</option>
                    <option v-for="society in filteredCustomerSocieties" :key="society.id" :value="society.id">
                        {{ society.name }}<template v-if="society.siret"> · SIRET {{ society.siret }}</template> · {{ society.city }}
                    </option>
                </select>
                <p v-if="selectedCustomerSociety" class="ckp-note">
                    {{ selectedCustomerSociety.address }}, {{ selectedCustomerSociety.postal_code }} {{ selectedCustomerSociety.city }}, {{ selectedCustomerSociety.country }}
                    <template v-if="selectedCustomerSociety.siren"> · SIREN {{ selectedCustomerSociety.siren }}</template><template v-if="selectedCustomerSociety.siret"> · SIRET {{ selectedCustomerSociety.siret }}</template>
                </p>
                <p v-if="customerSocieties.length === 0" class="ckp-note is-error">Aucune société active : créez-en une dans Clients › Sociétés.</p>
                <p v-else-if="invoiceRequiresSociety" class="ckp-note is-error">Choisissez une société pour générer la facture.</p>
            </div>
            <input v-if="(!sendInvoice && !productDepositMode) || (productDepositMode && productDepositModeLocked)" type="hidden" name="customer_society_id" :value="customer_society_id" />

            <div v-if="sendMail" class="ckp-options">
                <label class="ckp-check"><input id="rebooking" type="checkbox" @change="event => { parameterThanks.rebooking = event.target.checked }" /> Offre de rebooking</label>
                <label class="ckp-check"><input id="notice" type="checkbox" @change="event => { parameterThanks.notice = event.target.checked }" /> Liens des avis</label>
            </div>

            <input type="hidden" id="close_sale" name="close_sale" :value="closeSale" />
            <input type="hidden" name="parameterThanksMail" :value="JSON.stringify(parameterThanks)" />
            <input v-if="sendMail" type="hidden" id="send_mail" name="send_mail" :value="sendMail" />
            <input v-if="sendInvoice" type="hidden" id="send_invoice" name="send_invoice" :value="sendInvoice" />

            <button id="cashIn" type="button" class="ck-button ck-button-primary ck-button-block ck-button-large" @click="submitForm()" :disabled="invoiceRequiresSociety">
                {{ effectiveRemainPrice == 0 && !enteredPaymentAmount ? 'Clôturer la vente' : 'Encaisser ' + euro(enteredPaymentAmount || effectiveRemainPrice) }}
            </button>
            <div class="ckp-toggles">
                <button type="button" class="ck-button" :class="{ 'is-on': sendInvoice }" @click="() => {
                    sendInvoice = !sendInvoice
                    if (!sendInvoice && !productDepositMode) {
                        company_name = null
                        company_address = { address: null, cp: null, country: null }
                        customer_society_id = null
                    }
                }"><i v-if="sendInvoice" class="bi bi-check2"></i> Facture</button>
                <button type="button" class="ck-button" :class="{ 'is-on': sendMail }" @click="() => {
                    sendMail = !sendMail
                    if (!sendMail) parameterThanks = { rebooking: null, notice: null }
                }"><i v-if="sendMail" class="bi bi-check2"></i> Mail de remerciement</button>
            </div>
            <button
                v-if="effectiveRemainPrice <= 0"
                type="button"
                class="ckp-close-link"
                :class="{ 'is-on': closeSale }"
                @click="() => { closeSale = !closeSale }"
            >
                <i v-if="closeSale" class="bi bi-check2"></i>
                {{ closeSale ? 'La vente sera clôturée' : (enteredPaymentAmount ? 'Clôturer la vente après l’encaissement' : 'Clôturer sans encaisser') }}
            </button>
        </section>

        <!-- Déjà payé -->
        <section class="ckp-paid">
            <h3 class="ckp-title">Déjà payé</h3>
            <p v-if="paymentHistoryRows.length === 0" class="ckp-muted">Aucun paiement enregistré sur cette commande.</p>
            <div v-for="(item, index) in paymentHistoryRows" :key="item.id || index" class="ckp-row">
                <span>{{ parseDate(item.updated_at || item.created_at, 'D MMM YYYY · HH:mm') }}</span>
                <span>
                    <span class="bo-dt-badge">{{ item.payment_method?.name || 'Paiement' }}</span>
                    <template v-if="item.voucher_id"> {{ item.voucher }}</template>
                    <strong>{{ euro(paymentAmountAppliedToOrder(item)) }}</strong>
                    <small v-if="paymentHasPartiallyAppliedValue(item)">Valeur consommée : {{ euro(item.amount) }}</small>
                </span>
            </div>

            <template v-if="c_order && c_order.fingerprints && c_order.fingerprints.length > 0">
                <h3 class="ckp-title" style="margin-top: 14px;">Sécurisation</h3>
                <div v-for="(item, index) in c_order.fingerprints" :key="'f' + index" class="ckp-row">
                    <span>{{ parseDate(item.updated_at, 'D MMM YYYY · HH:mm') }}</span>
                    <span>
                        <span v-if="item.stripe_id" class="bo-dt-badge">Stripe</span>
                        <span v-if="item.voucher_id" class="bo-dt-badge">Bon {{ item.voucherCode }}</span>
                        <span v-if="item.serie_user_id" class="bo-dt-badge">Abonnement</span>
                        <span v-if="item.treatment_user_id" class="bo-dt-badge">Cure</span>
                        <strong>{{ euro(item.amount) }}</strong>
                        <FingerprintChange v-if="item.amount > 0" :item="item"></FingerprintChange>
                    </span>
                </div>
            </template>
        </section>

        <div :class="Object.values(staffModalData).every(s => s !== null) ? 'd-block' : 'd-none'">
            <staff-modal
                v-if="staffModalData.cartRowId && staffModalData.adviserId && staffModalData.sellerId"
                :cartRowId="staffModalData.cartRowId"
                :adviserId="staffModalData.adviserId"
                :sellerId="staffModalData.sellerId"
                @hide-staff-modal="hideStaffModal"
            ></staff-modal>
        </div>

        <dialog
            v-if="surplusToConfirm !== null"
            ref="surplusDialog"
            class="surplus-confirmation-modal rounded-24"
            aria-labelledby="surplus-confirmation-title"
            aria-describedby="surplus-confirmation-description"
            @cancel.prevent="closeSurplusModal"
            @keydown.tab="trapSurplusFocus"
        >
            <div class="modal-content border-0">
                <div class="modal-header">
                    <h3 id="surplus-confirmation-title" class="modal-title fs-5">Confirmer le surplus</h3>
                    <button type="button" class="btn-close" aria-label="Fermer" @click="closeSurplusModal"></button>
                </div>
                <div class="modal-body">
                    <p class="fw-semibold">Surplus de {{ surplusToConfirm }} €</p>
                    <p id="surplus-confirmation-description" class="mb-0">
                        Vous allez encaisser plus que la valeur du panier. Un avoir sera automatiquement créé pour ce surplus.
                    </p>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-alt-secondary" autofocus @click="closeSurplusModal">Annuler</button>
                    <button type="button" class="btn btn-warning text-white" @click="confirmSurplus">Confirmer l’encaissement</button>
                </div>
            </div>
        </dialog>

        <close-sale-modal
            v-if="showModalCloseSale"
            :closeSaleModal="showModalCloseSale"
            :companyName="company_name"
            :companyAddress="company_address"
            :customerSocietyId="customer_society_id"
            :customerSocieties="customerSocieties"
            :parameterThanks="parameterThanks"
            :sendInvoice="sendInvoice"
            :sendMail="sendMail"
            :requires-society="productDepositMode"
            :lock-society="productDepositMode && productDepositModeLocked"
            @close-sale="hideCloseSaleModal"
            @close-sale-modal="closeSaleModal"
        ></close-sale-modal>

        <!-- Libération d'un prépayé -->
        <div v-if="releasePrepaidModal" class="modal fade show d-block catalogue-modal" tabindex="-1" role="dialog" style="background: rgba(23, 27, 35, .45);">
            <div class="modal-dialog modal-dialog-centered" role="document">
                <div class="modal-content">
                    <div class="catalogue-modal-header">
                        <div>
                            <p class="catalogue-modal-eyebrow">Prépayé</p>
                            <h2 class="catalogue-modal-title">Libérer le prépayé</h2>
                            <p class="catalogue-modal-subtitle">
                                {{ releasePrepaidModal.label }}<template v-if="releasePrepaidModal.voucher_code"> · {{ releasePrepaidModal.voucher_code }}</template>
                                · {{ euro(prepaidReservationAmount(releasePrepaidModal)) }} : il ne sera plus déduit à la clôture.
                            </p>
                        </div>
                        <button type="button" class="catalogue-modal-close" aria-label="Fermer" :disabled="releasePrepaidLoading" @click="closeReleasePrepaidModal">×</button>
                    </div>
                    <div class="catalogue-modal-footer">
                        <button type="button" class="catalogue-modal-cancel" :disabled="releasePrepaidLoading" @click="closeReleasePrepaidModal">Annuler</button>
                        <button type="button" class="catalogue-modal-submit" :disabled="releasePrepaidLoading" @click="releasePrepaidReservation">
                            {{ releasePrepaidLoading ? 'Libération…' : 'Libérer' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment'
import { sync, get } from 'vuex-pathify'
import StaffModal from './StaffModal.vue'
import PaymentRefound from './PaymentRefound.vue'
import FingerprintChange from './FingerprintChange.vue'
import axios from 'axios'
import CloseSaleModal from './CloseSaleModal.vue'

export default {
    components: { StaffModal, PaymentRefound, FingerprintChange, CloseSaleModal },
    props: [
        'accompte',
        'rest',
        'totalAccompte',
        'totalWithDiscount',
        'cartTotal',
        'promoCode',
        'saveBasketRoute',
        'orderId',
        'isClotured',
        'orderObj',
        'customerSocieties',
        'voucherAlignmentDiscount',
    ],
    data: () => ({
        closeSale: false,
        showModalThanks: false,
        showModalCloseSale: false,
        surplusToConfirm: null,
        usedFidelityPoints: 0,
        isPrepaid: false,
        staffModalData: { cartRowId: null, adviserId: null, sellerId: null },
        parameterThanks: { rebooking: false, notice: false },
        invoices: [],
        customer_society_id: null,
        customer_society_search: '',
        company_name: null,
        company_address: { address: null, cp: null, country: null },
        sendInvoice: false,
        productDepositMode: false,
        sendMail: false,
        releasePrepaidModal: null,
        releasePrepaidLoading: false,
        releasePrepaidMessage: null,
    }),
    created() {
        this.restoreOrderBilling(this.orderObj || {})
        this.sendMail = false
    },
    computed: {
        ...get('overlay@data', {
            c_remainPrice: 'remainPrice',
            c_payments: 'payments',
            c_customer: 'customer',
            c_establishment: 'establishment',
            c_prepaidPrestations: 'prepaidPrestations',
            c_promoCodes: 'promoCodes',
            c_customDiscount: 'customDiscount',
            c_remainBeforeGlobalPromoCode: 'remainBeforeGlobalPromoCode',
            c_remainAfterGlobalPromoCode: 'remainAfterGlobalPromoCode',
            c_promoCodeNoCompleteUsed: 'promoCodeNoCompleteUsed',
            c_voucherUsed: 'voucherUsed',
        }),
        ...sync('overlay@data', {
            c_cartItems: 'cartItems',
            c_promoCodes: 'promoCodes',
            c_order: 'order',
        }),

        selectedCustomerSociety() {
            return (this.customerSocieties || []).find(society => Number(society.id) === Number(this.customer_society_id)) || null
        },
        invoiceRequiresSociety() {
            return (this.asBoolean(this.sendInvoice) || this.productDepositMode) && !this.customer_society_id
        },
        productDepositModeLocked() {
            return !!(this.isClotured || (this.c_order?.id ? this.c_order.has_fiscal_documents : this.orderObj?.has_fiscal_documents))
        },
        filteredCustomerSocieties() {
            const search = this.customer_society_search.toLowerCase().trim()
            if (!search) {
                return this.customerSocieties || []
            }

            return (this.customerSocieties || []).filter(society => [
                society.name,
                society.siren,
                society.siret,
                society.email,
                society.city,
                society.postal_code,
            ].filter(Boolean).join(' ').toLowerCase().includes(search))
        },

        maxFidelityPoint() {
            return this.c_customer.customer.loyalty_points
        },

        // Total des items en pending_removal
        pendingRemovalTotal() {
            return Object.values(this.c_cartItems)
                .filter(item => item.pendingRemoval)
                .reduce((sum, item) => sum + (item.price * item.qty), 0)
        },

        // Total des items en pending_addition
        pendingAdditionTotal() {
            return Object.values(this.c_cartItems)
                .filter(item => item.pendingAddition)
                .reduce((sum, item) => sum + (item.price * item.qty), 0)
        },

        pendingAdditionProductTotal() {
            return Object.values(this.c_cartItems)
                .filter(item => item.pendingAddition && item.options.type === 'App\\Models\\Product\\Product')
                .reduce((sum, item) => sum + (item.price * item.qty), 0)
        },

        pendingAdditionVoucherTotal() {
            return Object.values(this.c_cartItems)
                .filter(item => item.pendingAddition && item.options.type === 'App\\Models\\Voucher\\Voucher')
                .reduce((sum, item) => sum + (item.price * item.qty), 0)
        },

        pendingAdditionProductAndVoucherTotal() {
            return this.pendingAdditionProductTotal + this.pendingAdditionVoucherTotal
        },

        // Total affiché = base - suppressions en attente
        effectiveTotal() {
            const base = Math.round((this.c_remainAfterGlobalPromoCode + Number.EPSILON) * 100) / 100
            return (base - this.pendingRemovalTotal).toFixed(2)
        },

        remainClass() {
            return this.c_remainPrice > 0
                ? 'text-danger'
                : this.c_remainPrice < 0
                ? 'text-warning'
                : 'text-success'
        },

        showAlertPrepaid() {
            if (this.c_cartItems && this.c_prepaidPrestations) {
                const serieItems = Object.values(this.c_cartItems).filter(x => x.options.serie)
                const treatmentItems = Object.values(this.c_cartItems).filter(x => x.options.treatment)
                if (serieItems.length > 0 || treatmentItems.length > 0) {
                    this.isPrepaid = true
                    return true
                }
            }
            return false
        },

        prepaidTreatment() {
            if (this.c_prepaidPrestations)
                return this.c_prepaidPrestations
                    .filter(x => x.prepaid.type == 'treatment')
                    .map(x => ({ orderItem: x.prestation.id, userTreatment: x.prepaid.pivot.id }))
        },

        prepaidSerie() {
            if (this.c_prepaidPrestations)
                return this.c_prepaidPrestations
                    .filter(x => x.prepaid.type == 'serie')
                    .map(x => ({ orderItem: x.prestation.id, userSerie: x.prepaid.pivot.id }))
        },
        prepaidReservationsTotal() {
            return this.reservedPrepaidReservations
                .reduce((sum, r) => sum + this.prepaidReservationAmount(r), 0)
        },
        prepaidReservationVoucherCodes() {
            return [...new Set(
                this.reservedPrepaidReservations
                    .filter(r => r.voucher_code)
                    .map(r => r.voucher_code)
            )]
        },
        reservedPrepaidReservations() {
            if (!this.c_order || !this.c_order.prepaid_reservations) return []

            return this.c_order.prepaid_reservations
                .filter(r => r.status === 'reserved')
        },
        effectiveRemainPrice() {
            const remain = parseFloat(this.c_remainPrice)
            if (isNaN(remain)) return 0
            return remain // ← c_remainPrice est déjà correct
        },
        requiredProductAmount() {
            if (!this.c_cartItems) return 0

            return Object.values(this.c_cartItems)
                .filter(item => {
                    return item
                        && !item.pendingRemoval
                        && !item.invoice_id
                        && item.options?.type === 'App\\Models\\Product\\Product'
                })
                .reduce((sum, item) => {
                    const price = parseFloat(item.price || 0)
                    const quantity = parseInt(item.qty || item.quantity || 1)
                    return sum + price * quantity
                }, 0)
        },
        enteredPaymentAmount() {
            const paymentsAmount = this.c_payments
                ? this.c_payments
                    .filter(payment => {
                        if (!payment || payment.isRemisePersonalisee) return false
                        return !['Abonnement', 'Cure', 'serie', 'cure'].includes(payment.name)
                    })
                    .reduce((sum, payment) => sum + parseFloat(payment.value || 0), 0)
                : 0

            return paymentsAmount + this.fidelityPaymentAmount
        },
        fidelityPaymentAmount() {
            const pointsForOneEuro = parseFloat(this.c_establishment?.points_for_one_euro || 0)
            if (!this.usedFidelityPoints || pointsForOneEuro <= 0) return 0

            return parseFloat(this.usedFidelityPoints || 0) / pointsForOneEuro
        },
        nonFidelityInvoices() {
            return this.c_order?.invoices
                ? this.c_order.invoices.filter(invoice => !this.isFidelityInvoice(invoice))
                : []
        },
        fidelityHistoryAmount() {
            const invoiceTotal = this.c_order?.invoices
                ? this.c_order.invoices
                    .filter(invoice => this.isFidelityInvoice(invoice))
                    .reduce((sum, invoice) => sum + parseFloat(invoice.amount || 0), 0)
                : 0
            return invoiceTotal > 0 ? invoiceTotal : parseFloat(this.c_order?.fidelity_points || 0)
        },
        paymentHistoryRows() {
            const rows = this.invoices
                ? this.invoices.filter(invoice => !this.isFidelityInvoice(invoice))
                : []
            const fidelityAmount = this.fidelityHistoryAmount

            if (fidelityAmount > 0) {
                rows.unshift({
                    id: `fidelity-points-${this.c_order.id}`,
                    amount: fidelityAmount,
                    payment_method: { name: 'Points de fidélité' },
                    created_at: this.c_order.updated_at || this.c_order.created_at,
                    virtual_fidelity_points: true,
                })
            }

            return rows
        },
        hasUncoveredProductAmount() {
            return this.requiredProductAmount > 0
                && this.enteredPaymentAmount + 0.009 < this.requiredProductAmount
        },
        // effectiveRemainPrice() {
        //     // c_remainPrice inclut déjà la déduction via le parent
        //     // donc on retourne directement c_remainPrice
        //     const remain = parseFloat(this.c_remainPrice)
        //     if (isNaN(remain)) return '0.00'
        //     return remain.toFixed(2)
        // },
        
    },
    methods: {
        restoreOrderBilling(order) {
            this.sendInvoice = this.asBoolean(order.send_invoice)
            this.productDepositMode = this.asBoolean(order.product_deposit_mode)
            this.customer_society_id = order.customer_society_id
            this.company_name = order.company_name
            const address = (order.company_address || '').split('_')
            this.company_address = { address: address[0] || null, cp: address[1] || null, country: address[2] || null }
        },
        isFidelityInvoice(invoice) {
            const methodName = (invoice?.payment_method?.name || '').toLowerCase()
            return methodName.includes('fidélité') || invoice?.virtual_fidelity_points
        },
        paymentAmountAppliedToOrder(payment) {
            if (
                payment?.is_prepaid_consumption
                && payment.amount_applied_to_order !== null
                && payment.amount_applied_to_order !== undefined
            ) {
                return parseFloat(payment.amount_applied_to_order || 0)
            }

            return parseFloat(payment?.amount || 0)
        },
        paymentHasPartiallyAppliedValue(payment) {
            return payment?.is_prepaid_consumption
                && Math.abs(
                    parseFloat(payment.amount || 0)
                    - this.paymentAmountAppliedToOrder(payment)
                ) >= 0.01
        },
        getRefundable(item) {
            return item.amount > this.getTotalRefunded(item.id)
        },
        getRefundableAmount(item) {
            return Math.max(0, item.amount - this.getTotalRefunded(item.id))
        },
        getTotalRefunded(itemId) {
            if (!this.c_order || !this.c_order.invoices) return 0
            return this.c_order.invoices
                .filter(invoice => parseFloat(invoice.amount) < 0 && invoice.refunding_id === itemId)
                .reduce((sum, refund) => sum + Math.abs(parseFloat(refund.amount || 0)), 0)
        },
        stringify(val) {
            return JSON.stringify(val)
        },
        parseDate(date, format) {
            return moment(date).locale('fr').format(format)
        },
        submitForm() {
            console.log('encaisser')
            if (this.invoiceRequiresSociety) {
                return
            }

            if (this.effectiveRemainPrice < 0) {
                this.surplusToConfirm = Math.abs(this.effectiveRemainPrice).toFixed(2)
                this.$nextTick(() => this.$refs.surplusDialog.showModal())
                return
            }

            if (parseFloat(this.effectiveRemainPrice) == 0 && this.closeSale == false) {
                this.showModalCloseSale = true
            } else {
                this.$emit('submit-form')
            }
        },
        closeSurplusModal() {
            this.$refs.surplusDialog.close()
            this.surplusToConfirm = null
            this.$nextTick(() => this.$el.querySelector('#cashIn').focus())
        },
        trapSurplusFocus(event) {
            const buttons = this.$refs.surplusDialog.querySelectorAll('button:not([disabled])')
            const first = buttons[0]
            const last = buttons[buttons.length - 1]
            if ((event.shiftKey && event.target === first) || (!event.shiftKey && event.target === last)) {
                event.preventDefault()
                const target = event.shiftKey ? last : first
                target.focus()
            }
        },
        confirmSurplus() {
            if (this.surplusToConfirm === null) return
            const confirmedSurplus = this.surplusToConfirm
            this.closeSurplusModal()
            if (this.invoiceRequiresSociety) return
            if (this.effectiveRemainPrice < 0 && Math.abs(this.effectiveRemainPrice).toFixed(2) === confirmedSurplus) {
                this.$emit('submit-form')
            } else {
                this.submitForm()
            }
        },
        openStaffModal(cartRowId, adviserId, sellerId) {
            this.staffModalData = { cartRowId, adviserId, sellerId }
        },
        hideStaffModal() {
            Object.keys(this.staffModalData).forEach(key => { this.staffModalData[key] = null })
        },
        applySelectedSociety() {
            const society = this.selectedCustomerSociety
            this.company_name = society ? society.name : null
            this.company_address = society
                ? {
                    address: society.address,
                    cp: `${society.postal_code || ''} ${society.city || ''}`.trim(),
                    country: society.country,
                }
                : { address: null, cp: null, country: null }
        },
        hideCloseSaleModal(data) {
            this.closeSale = data.close_sale
            this.customer_society_id = data.customer_society_id
            this.company_name = data.company_name
            this.company_address = data.company_address
            this.parameterThanks.rebooking = data.rebooking
            this.parameterThanks.notice = data.notice
            this.showModalCloseSale = false
            this.sendMail = data.send_mail
            this.sendInvoice = this.asBoolean(data.send_invoice)
            setTimeout(() => { this.$emit('submit-form') }, 300)
        },
        closeSaleModal() {
            this.closeSale = false
            this.showModalCloseSale = false
        },
        getVoucherCode(id) {
            return axios.get(`/back-office/caisse/getVoucherCode/${id}`).then(res => res.data)
        },
        prepaidReservationAmount(reservation) {
            return parseFloat(reservation?.amount_covered ?? reservation?.amount_reserved ?? 0)
        },
        openReleasePrepaidModal(reservation) {
            this.releasePrepaidMessage = null
            this.releasePrepaidModal = reservation
        },
        closeReleasePrepaidModal() {
            if (this.releasePrepaidLoading) return
            this.releasePrepaidModal = null
        },
        releasePrepaidReservation() {
            if (!this.c_order || !this.releasePrepaidModal) return

            this.releasePrepaidLoading = true
            const reservation = this.releasePrepaidModal

            axios
                .post(`/back-office/commandes/${this.c_order.id}/prepaid-reservations/${reservation.id}/release`)
                .then(res => {
                    const updatedReservations = (this.c_order.prepaid_reservations || []).map(item => {
                        if (item.id !== reservation.id) return item

                        return {
                            ...item,
                            status: 'released',
                        }
                    })

                    this.c_order = {
                        ...this.c_order,
                        prepaid_reservations: updatedReservations,
                    }
                    this.releasePrepaidModal = null
                    this.releasePrepaidMessage = {
                        type: 'success',
                        text: res.data?.message || 'Prépayé libéré avec succès.',
                    }
                })
                .catch(error => {
                    this.releasePrepaidMessage = {
                        type: 'error',
                        text: error.response?.data?.message || "Impossible de libérer ce prépayé.",
                    }
                })
                .finally(() => {
                    this.releasePrepaidLoading = false
                })
        },
        asBoolean(value) {
            return value === true || value === 1 || value === '1'
        },
        euro(value) {
            const amount = parseFloat(value)
            return (isNaN(amount) ? 0 : amount).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
        },
        // Points couvrant le reste dû (hors points déjà saisis), plafonnés au solde du client
        useMaxFidelityPoints() {
            const rate = parseFloat(this.c_establishment?.points_for_one_euro || 0)
            if (rate <= 0) return
            const dueBeforePoints = this.effectiveRemainPrice + this.fidelityPaymentAmount
            const covering = Math.max(0, Math.floor(dueBeforePoints * rate + 0.0001))
            this.usedFidelityPoints = `${Math.min(this.maxFidelityPoint, covering)}`
        },
    },
    watch: {
        'c_order.id'(id, previousId) {
            if (id && id !== previousId) this.restoreOrderBilling(this.c_order)
        },
        c_remainPrice(val) {
            if (this.closeSale && val !== '0.00') {
                this.closeSale = false
            }
        },
        usedFidelityPoints(newVal) {
            this.$emit('used-fidelity-points', newVal)
        },
        async 'c_order.invoices'(val) {
            if (!val) return
            const copy = []
            for (let i = 0; i < val.length; i++) {
                const elem = val[i]
                if (elem.voucher_id) elem.voucher = await this.getVoucherCode(elem.voucher_id)
                copy.push(elem)
            }
            this.invoices = copy.reverse()
        },
    },
}
</script>

<style>
.surplus-confirmation-modal {
    width: min(500px, calc(100% - 2rem));
    max-height: calc(100% - 2rem);
    margin: auto;
    padding: 0;
    border: 0;
}
.surplus-confirmation-modal::backdrop {
    background: rgba(0, 0, 0, 0.45);
}
.disable-focus:focus {
    border-color: none !important;
    box-shadow: none !important;
}
.ckp-points input[type=number]::-webkit-inner-spin-button,
.ckp-points input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
</style>
